import { JournalJob, JournalTemplate, ListValueNote, ValidationError } from '../types/journal';

/**
 * What a passing "Check for errors" says. Without the ERP's own check it must not read as clearance
 * to send: periods, blocked accounts and posting rules are the ERP's, and it applies them when the
 * journal is sent.
 */
export function validationPassedMessage(lineCount: number, ready: string, warningCount: number, erpChecked: boolean): string {
  const warnings = warningCount > 0 ? ` (${warningCount} warning(s) below)` : '';
  const scope = erpChecked
    ? 'The ERP checked them too.'
    : 'Your ERP checks its own rules (periods, blocked accounts, posting rules) when the journal is sent.';
  return `Validation passed — ${lineCount} lines ${ready}${warnings}. ${scope}`;
}

/** A send of a version the ERP has posted, from Bifrost or in the ERP itself; nothing was posted this time. */
export function alreadyPostedMessage(job: Pick<JournalJob, 'erpResponse'>): string {
  if (job.erpResponse?.postedWithChanges) {
    return 'Already posted in the ERP, but it was changed there first, so what was posted is not exactly this sheet. Check it in the ERP.';
  }
  return job.erpResponse?.postedInErp ? 'Already posted in the ERP.' : 'Already posted to the ERP.';
}

/**
 * What the pane says after a send: the ERP's reference, what the send did besides in its ordinary
 * course (an earlier version's draft removed for this one), and how many warnings are listed below.
 */
export function sentMessage(success: string, reference: string | undefined, notes: string[], warnings: number): string {
  const ref = reference ? ` ERP reference: ${reference}.` : '';
  const told = notes.length > 0 ? ` ${notes.join(' ')}` : '';
  const listed = warnings > 0 ? ` ${warnings} warning(s) below.` : '';
  return `${success}${ref}${told}${listed}`;
}

/** Beside Push and Finalize & post, greyed out for whoever prepared the sheet's journal or sent it for review. */
export const OWN_JOURNAL_UNDER_REVIEW_NOTE = 'You prepared this journal or sent it for review, so a colleague with push access sends it to your ERP.';

/** Over a passing check's message once its sheet is edited: the pass was for what the sheet read then. */
export const EDITED_SINCE_CHECK_MESSAGE = 'Edited since the check passed. If its lines changed, it is checked again when you send.';

/** Who a journal action would go out as, and who the Journal tab was read for. */
interface SignedInAs {
  email?: string;
  company?: string;
  companyId?: string;
}

/**
 * Over a pane drawn again because the add-in is signed in as another user, or to another company, than
 * the Journal tab was read for. Every open workbook shares the one sign-in, and the company picker at
 * the top of the pane is where a company is switched back.
 */
export function signedInElsewhereMessage(now: SignedInAs, before: SignedInAs): string {
  if (now.companyId === before.companyId) {
    return `The add-in is now signed in as ${now.email || 'another user'}, perhaps from another workbook, so nothing was done and the pane was refreshed.`;
  }
  return `The add-in is now signed in to ${now.company || 'another company'}, perhaps from another workbook, so nothing was done and the pane was refreshed. `
    + `To carry on with this sheet, switch back to ${before.company || 'the company it was made for'} at the top of the pane.`;
}

/** A journal click, Refresh or company switch while a journal action is still running in this pane. */
export const JOURNAL_BUSY_MESSAGE = 'A journal action is still running. Try again when it has finished.';

/** A journal click while the pane switches company, which replaces the session and the Journal tab under it. */
export const SWITCHING_COMPANY_MESSAGE = 'The add-in is switching company. Try again when it has finished.';

/** A template deactivated or removed in the Bifrost Portal since the pane loaded its list. */
export const TEMPLATE_INACTIVE_MESSAGE = 'This template is no longer active in the Bifrost Portal, so nothing was done and the template list was reloaded. '
  + 'Pick an active template, or ask an admin to activate this one again.';

/** The ERP connection the Journal tab's rights named was disconnected or replaced since the tab was read. */
export const CONNECTION_INACTIVE_MESSAGE = 'The ERP connection this journal would go to is no longer active, so nothing was sent and the Journal tab was reloaded. '
  + 'Try again, or ask an admin to reconnect the ERP in the Bifrost Portal.';

/** Shown the moment Push is clicked, while what it would do is still being worked out. */
export const CHECKING_MESSAGE = 'Checking...';

/** The first Push click for a journal whose ERP posts when it is sent, and one too soon after it. */
export const POSTS_AT_ONCE_WARNING = 'This posts to your ERP immediately and cannot be undone. Click again to confirm.';

/** Finalize & post for a journal whose ERP posts when it is sent: there is no draft, and Push asks first. */
export const FINALIZE_POSTS_AT_ONCE_MESSAGE = 'This journal\'s ERP posts it as soon as it is sent, so there is no draft to finalize. '
  + 'Use Post to ERP, which asks you to confirm first.';

export function countBySeverity(errors: ValidationError[]): { errors: number; warnings: number } {
  const blocking = errors.filter(e => e.severity === 'error').length;
  return { errors: blocking, warnings: errors.length - blocking };
}

/** Counts only what is listed under it, errors and warnings apart. */
export function validationFoundMessage(errors: ValidationError[]): string {
  const counts = countBySeverity(errors);
  const warnings = counts.warnings > 0 ? ` and ${counts.warnings} warning(s)` : '';
  return `Validation found ${counts.errors} error(s)${warnings}`;
}

/** Errors before warnings; within each, the journal-level ones first, as they map to no cell. */
export function errorsForDisplay(errors: ValidationError[]): ValidationError[] {
  const rank = (e: ValidationError) => (e.severity === 'error' ? 0 : 2) + (e.lineNumber === 0 ? 0 : 1);
  return errors
    .map((e, index) => ({ e, index }))
    .sort((a, b) => rank(a.e) - rank(b.e) || a.index - b.index)
    .map(({ e }) => e);
}

/** A column as the sheet heads it; the template's key is never shown. */
export function columnLabelOf(template: JournalTemplate | null, key: string): string {
  return template?.columnDefinitions.find(c => c.name === key)?.label ?? key;
}

/** "Line 1 — CostCenter", "Journal", "Warning, line 2 — VAT code", "Warning". */
export function errorLocation(error: ValidationError, template: JournalTemplate | null): string {
  const warning = error.severity === 'warning';
  if (error.lineNumber === 0) return warning ? 'Warning' : 'Journal';
  const where = warning ? `Warning, line ${error.lineNumber}` : `Line ${error.lineNumber}`;
  return error.column ? `${where} — ${columnLabelOf(template, error.column)}` : where;
}

export function errorListHeading(errors: ValidationError[]): string {
  const counts = countBySeverity(errors);
  if (counts.errors === 0) return 'Warnings';
  return counts.warnings > 0 ? 'Errors and warnings' : 'Validation errors';
}

/**
 * What the pane says when a typed value is not in its dropdown list. Not a refusal: the list may
 * predate a value the ERP has since added, which the ERP's own check accepts.
 */
export function listNoteMessage(notes: ListValueNote[], template: JournalTemplate | null): string {
  const [first] = notes;
  if (!first) return '';
  const more = notes.length > 1 ? ` (${notes.length - 1} more marked)` : '';
  return `Line ${first.lineNumber} — ${columnLabelOf(template, first.column)}: ${first.value} is not in the dropdown list${more}. Check for errors asks the ERP.`;
}

/** What the pane says when values missing from their lists were found in the ERP once the lists were read again. */
export function listNotesFoundMessage(found: ListValueNote[], remaining: ListValueNote[], template: JournalTemplate | null): string {
  const [first] = found;
  if (!first) return '';
  const values = found.length > 1 ? `${first.value} and ${found.length - 1} more` : first.value;
  const columns = new Set(found.map(n => n.column));
  const dropdowns = columns.size === 1 ? `${columnLabelOf(template, first.column)} dropdown` : 'dropdowns';
  const still = remaining.length > 0 ? ` ${listNoteMessage(remaining, template)}` : '';
  return `${values} found in the ERP — ${dropdowns} updated.${still}`;
}

/** What the pane says after "Refresh dropdowns": what came from the ERP, and what is still not in it. */
export function listsRefreshedMessage(refresh: { expected: number; failed: number }, notes: ListValueNote[], template: JournalTemplate | null): string {
  const still = notes.length > 0 ? ` ${listNoteMessage(notes, template)}` : '';
  if (refresh.expected === 0) return 'This sheet has no dropdowns to refresh.';
  if (refresh.failed === refresh.expected) return 'The dropdown lists could not be read from the ERP. Check for errors still checks every value.';
  if (refresh.failed > 0) {
    return `Dropdowns refreshed from the ERP, but ${refresh.failed} of ${refresh.expected} lists could not be read.${still}`;
  }
  return `Dropdowns refreshed from the ERP.${still}`;
}

/** A sheet whose columns no longer line up with its template, where values would be read into the wrong fields. */
export function sheetMisfitMessage(worksheetName: string): string {
  return `The columns in "${worksheetName}" no longer match the template. Insert a new journal sheet and copy your lines into it.`;
}
